import { useState, ChangeEvent, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';

import { knmListByPlanCommentKnmAction, knmListByPlanExcludeAction } from '../actions/knmListByPlanActions';
import styles from './KnmListByPlanCommentModal.module.scss';

interface IKnmListByPlanCommentModalProps {
  visible: boolean;
  isExcludeComment: boolean;
  itemsCheck: TableCheckItems;
  onClose: () => void;
  knmListByPlanCommentKnm: (knmIds: number[], note?: string) => void;
}

const KnmListByPlanCommentModalWithoutStore = ({
  visible,
  itemsCheck,
  isExcludeComment,
  onClose,
  knmListByPlanCommentKnm,
}: IKnmListByPlanCommentModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [inputValue, setInputValue] = useState('');
  const [isShowCommentRequiredError, setIShowCommentRequiredError] = useState(false);
  const knmIds = Object.keys(itemsCheck)
    .filter(key => itemsCheck[key])
    .map(item => +item);

  const handleAfterClose = useCallback(() => {
    setInputValue('');
  }, []);

  const handleSaveButton = useCallback(() => {
    if (!inputValue) {
      setIShowCommentRequiredError(true);

      return;
    }
    setIShowCommentRequiredError(false);

    if (knmIds) {
      if (isExcludeComment) {
        dispatch(
          knmListByPlanExcludeAction({
            ids: knmIds.map(item => item.toString()),
            note: inputValue,
          }),
        );
      } else {
        knmListByPlanCommentKnm(knmIds, inputValue);
      }
    }
    onClose();
  }, [dispatch, inputValue, isExcludeComment, knmIds, knmListByPlanCommentKnm, onClose]);

  const handleChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (!e.target.value) {
      setIShowCommentRequiredError(true);
    } else {
      setIShowCommentRequiredError(false);
    }
  }, []);

  return (
    <Modal
      modalBodyClassName={styles.ModalBody}
      visible={visible || isExcludeComment}
      onClose={onClose}
      afterClose={handleAfterClose}
    >
      <ModalHeader sizeText="small" title="Замечание для выбранных кнм" />
      <ModalBody size="content" border="bottom">
        <Row>
          <FieldBlock>
            <TextInput
              className={styles.Input}
              placeholder="Введите комментарий"
              onChange={handleChangeInput}
              value={inputValue}
            />
            {isShowCommentRequiredError && (
              <p className="shared-input-error shared-row__small-margin">{'Поле не может быть пустым'}</p>
            )}
          </FieldBlock>
        </Row>
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" onClick={onClose} />
        <Button
          disabled={isExcludeComment ? false : !inputValue}
          id="saveButton"
          text="Сохранить"
          size="medium"
          onClick={handleSaveButton}
        />
      </ModalActions>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<RootActions>) => ({
  knmListByPlanCommentKnm: (knmIds: number[], note?: string) => dispatch(knmListByPlanCommentKnmAction(knmIds, note)),
});

export const KnmListByPlanCommentModal = connect(null, mapDispatchToProps)(KnmListByPlanCommentModalWithoutStore);
